import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Button,
  TextField,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Payment as PaymentIcon,
  LocalOffer as DiscountIcon,
  Close as CloseIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import {
  InputAdornment,
  Chip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store/useStore';
import { useMutation } from '@tanstack/react-query';
import { firebaseOrdersService } from '../services/firebaseService';
import { firebaseShippingService } from '../services/firebaseShippingService';
import { formatWeightKg, SHIPPING_ZONE_LABELS } from '../utils/shippingUtils';
import { formatWineSubtitle } from '../utils/wineUtils';
import { firebaseDiscountService } from '../services/firebaseDiscountService';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise from '../config/stripe';
import StripePayment from '../components/checkout/StripePayment';
import { sendOrderConfirmationEmail } from '../services/emailClient';

const steps = ['Información de envío', 'Resumen del pedido', 'Pago', 'Confirmación'];

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, user, clearCart } = useStore();
  const [activeStep, setActiveStep] = useState(0);
  const [orderData, setOrderData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    province: '',
    notes: ''
  });
  const [successDialog, setSuccessDialog] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [orderProcessed, setOrderProcessed] = useState(false);
  
  // Estados para envío dinámico y descuentos
  const [shippingDetails, setShippingDetails] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null);
  const [discountError, setDiscountError] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const [countdownInterval, setCountdownInterval] = useState(null);
  // Mutation para crear pedido
  const createOrderMutation = useMutation({
    mutationFn: firebaseOrdersService.create,
    onSuccess: (response) => {
      setCreatedOrder(response.data);
      setOrderProcessed(true);
      setSuccessDialog(true);
      // Solo limpiar el carrito cuando el usuario confirme que vio el pedido exitoso
    },
    onError: (error) => {
      console.error('Error al crear pedido:', error);
    }
  });

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(price);
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => shippingDetails?.cost ?? 0;

  useEffect(() => {
    if (cart.length === 0) {
      setShippingDetails(null);
      return;
    }

    let cancelled = false;

    firebaseShippingService
      .calculateForCart(cart, {
        postalCode: orderData.postalCode,
        province: orderData.province
      })
      .then((result) => {
        if (!cancelled) setShippingDetails(result);
      })
      .catch((error) => {
        console.error('Error al calcular envío:', error);
        if (!cancelled) setShippingDetails(null);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart, orderData.postalCode, orderData.province]);

  const calculateDiscount = () => {
    return appliedDiscount ? appliedDiscount.discount : 0;
  };

  const calculateTotal = () => {
    return Math.max(0, calculateSubtotal() + calculateShipping() - calculateDiscount());
  };

  // Aplicar código de descuento
  const applyDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Por favor ingresa un código de descuento');
      return;
    }

    try {
      setDiscountError('');
      const subtotal = calculateSubtotal();
      const result = await firebaseDiscountService.validateAndApply(discountCode, subtotal);
      
      if (result.valid) {
        setAppliedDiscount(result);
        setDiscountError('');
        // toast.success ya no está disponible aquí, usamos console.log
        console.log('✅ Código aplicado:', result.code);
      } else {
        setDiscountError(result.error);
        setAppliedDiscount(null);
      }
    } catch (error) {
      console.error('Error al aplicar descuento:', error);
      setDiscountError('Error al validar el código');
      setAppliedDiscount(null);
    }
  };

  // Remover código de descuento
  const removeDiscountCode = () => {
    setAppliedDiscount(null);
    setDiscountCode('');
    setDiscountError('');
  };

  const handleInputChange = (field) => (event) => {
    setOrderData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validar información de envío
      if (!orderData.name || !orderData.email || !orderData.phone || 
          !orderData.address || !orderData.city || !orderData.postalCode) {
        return;
      }
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setPaymentError(null); // Limpiar errores de pago al navegar
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const [paymentToken, setPaymentToken] = useState(null);
  const [paymentProcessing, setPaymentProcessing] = useState(false);

  const isPaymentVerified = (payment) => {
    if (!payment?.id) return false;
    if (payment.devMode) {
      return process.env.NODE_ENV === 'development';
    }
    return payment.paymentStatus === 'succeeded';
  };

  const handlePaymentSuccess = async (paymentResult) => {
    if (!isPaymentVerified(paymentResult)) {
      setPaymentError('El pago no se completó. No se ha registrado ningún pedido.');
      return;
    }

    setPaymentError(null);
    setPaymentToken(paymentResult);
    setPaymentProcessing(true);

    try {
      await handleCreateOrder(paymentResult);
      setActiveStep(3);
    } catch {
      setActiveStep(2);
    } finally {
      setPaymentProcessing(false);
    }
  };

  const [paymentError, setPaymentError] = useState(null);

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setPaymentError(error.message || 'Error en el procesamiento del pago');
  };

  const handleCreateOrder = async (paymentTokenParam = null) => {
    const payment = paymentTokenParam || paymentToken;
    if (!isPaymentVerified(payment)) {
      throw new Error('No se puede crear el pedido sin un pago confirmado.');
    }

    console.log('🔄 Iniciando creación de pedido tras pago confirmado...');
    setPaymentProcessing(true);

    const order = {
      items: cart.map(item => ({
        wineId: item.id,
        wineName: item.name,
        price: item.price,
        quantity: item.quantity
      })),
      shippingAddress: {
        name: orderData.name,
        email: orderData.email,
        phone: orderData.phone,
        street: orderData.address,
        city: orderData.city,
        postalCode: orderData.postalCode,
        province: orderData.province
      },
      paymentMethod: payment.method || 'stripe',
      paymentIntentId: payment.id,
      paymentToken: payment.id,
      paymentStatus: 'paid',
      paymentDevMode: payment.devMode || false,
      paidAt: new Date().toISOString(),
      notes: orderData.notes,
      subtotal: calculateSubtotal(),
      shipping: calculateShipping(),
      discount: calculateDiscount(),
      discountCode: appliedDiscount?.code || null,
      total: calculateTotal(),
      shippingZone: shippingDetails?.zone || 'peninsula',
      shippingRateName: shippingDetails?.rateName || '',
      shippingDetails: shippingDetails
        ? {
            zone: shippingDetails.zone,
            zoneLabel: SHIPPING_ZONE_LABELS[shippingDetails.zone] || shippingDetails.zone,
            rateName: shippingDetails.rateName,
            weightKg: shippingDetails.weightKg,
            bottles: shippingDetails.bottles,
            bracketLabel: shippingDetails.bracketLabel,
            pricingMethod: shippingDetails.pricingMethod,
            cost: shippingDetails.cost,
            freeShipping: shippingDetails.freeShipping,
            freeShippingThreshold: shippingDetails.freeShippingThreshold,
            originalCost: shippingDetails.originalCost,
            deliveryDays: shippingDetails.deliveryDays
          }
        : null,
      isGuest: !user?.uid,
      userId: user?.uid || null,
      customerName: orderData.name,
      customerEmail: orderData.email,
      status: 'confirmed'
    };

    console.log('📋 Datos del pedido:', order);

    try {
      console.log('🔄 Creando pedido con validación y reducción de stock automática...');
      // Crear pedido con validación y reducción de stock automática
      const orderResponse = await firebaseOrdersService.create(order);
      console.log('✅ Pedido creado exitosamente:', orderResponse.data);
      
      // Enviar email de confirmación (EmailJS)
      try {
        await sendOrderConfirmationEmail({
          ...order,
          id: orderResponse.data.data.id
        });
        console.log('✅ Email de confirmación enviado');
      } catch (e) {
        console.error('❌ Error enviando email de confirmación:', e);
      }
      
      // Marcar código de descuento como usado
      if (appliedDiscount?.code) {
        try {
          console.log('🔄 Marcando código de descuento como usado...');
          await firebaseDiscountService.markAsUsed(appliedDiscount.code);
          console.log('✅ Código de descuento marcado como usado:', appliedDiscount.code);
        } catch (discountError) {
          console.warn('⚠️ Error al marcar código como usado:', discountError);
          // No fallar el pedido por esto
        }
      }
      
      const savedOrder = orderResponse.data?.data || {};
      setCreatedOrder({
        id: savedOrder.id,
        total: savedOrder.total ?? order.total,
        status: savedOrder.status ?? order.status
      });
      setOrderProcessed(true);
      console.log('✅ Pedido procesado exitosamente');
      // No abrimos el diálogo automáticamente ya que todo se maneja en la página de confirmación
      
      // El usuario verá la pantalla de confirmación y podrá hacer clic en "Volver a la tienda"
      // No redirigir automáticamente aquí
    } catch (error) {
      console.error('❌ Error al crear pedido:', error);
      console.error('❌ Detalles del error:', {
        message: error.message,
        code: error.code,
        stack: error.stack
      });
      setPaymentProcessing(false);
      
      // Mensajes de error más específicos
      let errorMessage = 'Error al crear el pedido. ';
      if (error.message?.includes('Stock insuficiente')) {
        errorMessage = '🚨 ' + error.message + '. Por favor, ajusta las cantidades en tu carrito y vuelve a intentarlo.';
      } else if (error.message?.includes('permission')) {
        errorMessage += 'Error de permisos. Verifica tu conexión.';
      } else if (error.message?.includes('network')) {
        errorMessage += 'Error de conexión. Verifica tu internet.';
      } else if (error.message?.includes('firebase')) {
        errorMessage += 'Error en la base de datos. Inténtalo de nuevo.';
      } else {
        errorMessage += 'Por favor, inténtalo de nuevo.';
      }
      
      setPaymentError(errorMessage);
      throw error;
    } finally {
      setPaymentProcessing(false);
    }
  };

  const handleCloseDialog = () => {
    // Limpiar el contador si está activo
    if (countdownInterval) {
      clearInterval(countdownInterval);
      setCountdownInterval(null);
    }
    
    setSuccessDialog(false);
    clearCart(); // Limpiar carrito después de completar el pedido
    // Todos los usuarios (autenticados y no autenticados) van a la página principal
    navigate('/');
  };

  // Manejar navegación cuando el carrito está vacío
  useEffect(() => {
    if (cart.length === 0 && !orderProcessed) {
      navigate('/');
    }
  }, [cart.length, orderProcessed, navigate]);

  // Redireccionamiento automático tras completar el pedido
  useEffect(() => {
    if (orderProcessed) {
      setRedirectCountdown(5);
      const interval = setInterval(() => {
        setRedirectCountdown(prev => {
          if (prev === 1) {
            clearInterval(interval);
            navigate('/');
            return null;
          }
          return prev - 1;
        });
      }, 1000);
      setCountdownInterval(interval);
      return () => clearInterval(interval);
    }
  }, [orderProcessed, navigate]);

  // Mostrar loading mientras se navega
  if (cart.length === 0 && !orderProcessed) {
    return (
      <Container maxWidth="md" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Redirigiendo...
        </Typography>
      </Container>
    );
  }

  const renderShippingForm = () => (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Información de envío
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Nombre completo"
              value={orderData.name}
              onChange={handleInputChange('name')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={orderData.email}
              onChange={handleInputChange('email')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Teléfono"
              value={orderData.phone}
              onChange={handleInputChange('phone')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Código postal"
              value={orderData.postalCode}
              onChange={handleInputChange('postalCode')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Dirección"
              value={orderData.address}
              onChange={handleInputChange('address')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ciudad"
              value={orderData.city}
              onChange={handleInputChange('city')}
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Provincia"
              value={orderData.province}
              onChange={handleInputChange('province')}
            />
          </Grid>
          {shippingDetails && (
            <Grid item xs={12}>
              <Alert severity="info">
                <Typography variant="body2">
                  <strong>Zona detectada:</strong>{' '}
                  {SHIPPING_ZONE_LABELS[shippingDetails.zone] || shippingDetails.zone}
                  {orderData.postalCode ? ` (CP ${orderData.postalCode})` : ''}
                  <br />
                  <strong>Peso del pedido:</strong> {formatWeightKg(shippingDetails.weightKg)} (
                  {shippingDetails.bottles} botella{shippingDetails.bottles !== 1 ? 's' : ''})
                  {shippingDetails.bracketLabel && (
                    <>
                      <br />
                      <strong>Tarifa aplicada:</strong> {shippingDetails.bracketLabel} —{' '}
                      {shippingDetails.freeShipping
                        ? 'Envío gratis'
                        : formatPrice(shippingDetails.cost)}
                    </>
                  )}
                </Typography>
              </Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notas del pedido (opcional)"
              multiline
              rows={3}
              value={orderData.notes}
              onChange={handleInputChange('notes')}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderOrderSummary = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Productos en tu pedido
            </Typography>
            <List>
              {cart.map((item, index) => (
                <ListItem key={item.id} divider={index < cart.length - 1}>
                  <ListItemAvatar>
                    <Avatar
                      src={item.imageUrl || '/images/wine-placeholder.jpg'}
                      alt={item.name}
                      variant="rounded"
                      sx={{ width: 60, height: 60 }}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={item.name}
                    secondary={formatWineSubtitle(item)}
                    sx={{ ml: 2 }}
                  />
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2">
                      {item.quantity} x {formatPrice(item.price)}
                    </Typography>
                    <Typography variant="h6" color="primary">
                      {formatPrice(item.price * item.quantity)}
                    </Typography>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>

        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Información de envío
            </Typography>
            <Typography variant="body2">
              <strong>{orderData.name}</strong><br />
              {orderData.email}<br />
              {orderData.phone}<br />
              {orderData.address}<br />
              {orderData.city}, {orderData.postalCode}
              {orderData.province && `, ${orderData.province}`}
            </Typography>
            {shippingDetails && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Envío:</strong>{' '}
                  {SHIPPING_ZONE_LABELS[shippingDetails.zone] || shippingDetails.zone}
                  {' · '}
                  {formatWeightKg(shippingDetails.weightKg)}
                  {shippingDetails.bracketLabel && ` · ${shippingDetails.bracketLabel}`}
                  {' — '}
                  {shippingDetails.freeShipping ? 'Gratis' : formatPrice(shippingDetails.cost)}
                </Typography>
              </Box>
            )}
            {orderData.notes && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Notas:</strong> {orderData.notes}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Resumen del pedido
          </Typography>

          {/* Código de descuento */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              ¿Tienes un código de descuento?
            </Typography>
            {!appliedDiscount ? (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  size="small"
                  placeholder="Código promocional"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  error={!!discountError}
                  helperText={discountError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DiscountIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={applyDiscountCode}
                  disabled={!discountCode.trim()}
                >
                  Aplicar
                </Button>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={`${appliedDiscount.code} aplicado`}
                  color="success"
                  onDelete={removeDiscountCode}
                  deleteIcon={<CloseIcon />}
                />
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">{formatPrice(calculateSubtotal())}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color={calculateShipping() === 0 ? 'success.main' : 'text.primary'}>
                Envío ({shippingDetails?.rateName || '—'})
              </Typography>
              <Typography variant="body2" color={calculateShipping() === 0 ? 'success.main' : 'text.primary'}>
                {calculateShipping() === 0 ? 'Gratis' : formatPrice(calculateShipping())}
              </Typography>
            </Box>
            {shippingDetails?.bracketLabel && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {SHIPPING_ZONE_LABELS[shippingDetails.zone]} · {shippingDetails.bracketLabel} ·{' '}
                {formatWeightKg(shippingDetails.weightKg)}
              </Typography>
            )}
            {appliedDiscount && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="success.main">
                  Descuento ({appliedDiscount.code})
                </Typography>
                <Typography variant="body2" color="success.main">
                  -{formatPrice(calculateDiscount())}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Total</Typography>
            <Typography variant="h6" color="primary">
              {formatPrice(calculateTotal())}
            </Typography>
          </Box>

          <Button
            variant="contained"
            fullWidth
            onClick={() => setActiveStep(2)}
            startIcon={<PaymentIcon />}
            sx={{ py: 1.5 }}
          >
            Proceder al Pago
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderPayment = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4 }}>
      <Paper
        elevation={4}
        sx={{
          p: 5,
          borderRadius: 4,
          minWidth: 400,
          maxWidth: 480,
          textAlign: 'center',
          background: 'rgba(255,255,255,0.98)',
          boxShadow: '0 8px 32px rgba(114,47,55,0.10)'
        }}
      >
        <Typography variant="h4" sx={{ mb: 2, fontFamily: '"Playfair Display", serif', color: 'secondary.main', fontWeight: 700 }}>
          Pago seguro
        </Typography>
        <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
          Paga con tarjeta de débito o crédito. El cobro se procesa con Stripe de manera segura.
        </Typography>
        <Divider sx={{ mb: 3 }} />

        {!stripePromise ? (
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 480 }}>
            Falta configurar la clave pública de Stripe (REACT_APP_STRIPE_PUBLIC_KEY). Usa la clave
            pk_live de la misma cuenta que la clave secreta en Stripe Dashboard.
          </Alert>
        ) : null}

        <Elements stripe={stripePromise}>
          <StripePayment
            amount={calculateTotal()}
            orderData={orderData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
            isProcessing={paymentProcessing}
          />
        </Elements>
        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'success.main', mt: 2 }}>
          <LockIcon sx={{ mr: 1 }} />
          <Typography variant="body2" color="success.main">
            Pago 100% seguro con cifrado SSL
          </Typography>
        </Box>
      </Paper>
    </Box>
  );

  const renderConfirmation = () => (
    <Card>
      <CardContent sx={{ textAlign: 'center', py: 6 }}>
        {!createOrderMutation.isPending && !orderProcessed ? (
          <CircularProgress color="primary" sx={{ fontSize: 80, mb: 3 }} />
        ) : orderProcessed ? (
          <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 3 }} />
        ) : (
          <CircularProgress color="primary" sx={{ fontSize: 80, mb: 3 }} />
        )}
        
        <Typography variant="h4" sx={{ mb: 2, fontFamily: '"Playfair Display", serif' }}>
          {orderProcessed ? '¡Pedido Creado Exitosamente!' : '¡Pago Completado!'}
        </Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          {orderProcessed ? 
            'Tu pedido ha sido creado correctamente y recibirás un email de confirmación.' :
            createOrderMutation.isPending ? 
              'Creando tu pedido... Por favor espera.' :
              'Tu pago ha sido procesado exitosamente. Creando pedido automáticamente...'
          }
        </Typography>
        
        {createOrderMutation.isPending && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Procesando pedido...
            </Typography>
          </Box>
        )}
        
                          {orderProcessed && createdOrder && (
           <Box sx={{ mt: 3 }}>
             <Typography variant="body2" color="text.secondary">
               Número de pedido: <strong>#{createdOrder.id}</strong>
             </Typography>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
               Total: <strong>{formatPrice(createdOrder.total || calculateTotal())}</strong>
             </Typography>
             
             <Alert severity="success" sx={{ mb: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
               <Typography variant="body2" sx={{ textAlign: 'center' }}>
                 🎉 ¡Pedido completado exitosamente!<br/>
                 📧 Recibirás un email de confirmación en: <strong>{orderData.email}</strong><br/>
                 {redirectCountdown !== null ? (
                   <>🔄 Redirigiendo automáticamente en <strong>{redirectCountdown}</strong> segundos...</>
                 ) : (
                   <>🔄 Preparando redirección...</>
                 )}
               </Typography>
             </Alert>
             
             <Button
               variant="contained"
               size="large"
               onClick={handleCloseDialog}
               sx={{ mt: 1, whiteSpace: 'normal', wordBreak: 'break-word' }}
             >
               Volver a la Tienda Ahora
             </Button>
           </Box>
         )}
         
         {paymentError && !orderProcessed && activeStep === 3 && (
           <Box sx={{ mt: 3 }}>
             <Alert severity="error" sx={{ mb: 2 }}>
               {paymentError}
             </Alert>
             <Button
               variant="contained"
               onClick={() => {
                 setPaymentError(null);
                 setActiveStep(2);
               }}
             >
               Volver al pago
             </Button>
           </Box>
         )}
      </CardContent>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 4, fontFamily: '"Playfair Display", serif' }}>
        Finalizar Compra
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && renderShippingForm()}
      {activeStep === 1 && renderOrderSummary()}
      {activeStep === 2 && renderPayment()}
      {activeStep === 3 && renderConfirmation()}

      {activeStep < 3 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            onClick={activeStep === 0 ? () => navigate('/cart') : handleBack}
            startIcon={<ArrowBackIcon />}
          >
            {activeStep === 0 ? 'Volver al carrito' : 'Anterior'}
          </Button>

          {activeStep < 2 && (
            <Button variant="contained" onClick={handleNext}>
              Siguiente
            </Button>
          )}
        </Box>
      )}

      {/* Dialog de confirmación - Solo para casos de error o casos especiales */}
      {successDialog && (
        <Dialog open={successDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
            </Box>
            ¡Pedido creado exitosamente!
          </DialogTitle>
          <DialogContent>
            {createdOrder && (
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tu pedido <strong>#{createdOrder.id}</strong> ha sido creado correctamente.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recibirás un email de confirmación en <strong>{orderData.email}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Total: <strong>{formatPrice(createdOrder.total || calculateTotal())}</strong>
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button variant="contained" onClick={handleCloseDialog}
              sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}
            >
              Volver a la tienda
            </Button>
          </DialogActions>
        </Dialog>
      )}


    </Container>
  );
};

export default Checkout; 